import React, { useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { FiPlus, FiCreditCard, FiChevronLeft, FiChevronRight, FiUpload } from 'react-icons/fi';
import AppLayout from '../../components/layout/AppLayout';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import DataTable from '../../components/ui/DataTable';
import { Select } from '../../components/ui/Input';
import StatusPill from '../../components/ui/StatusPill';
import EmptyState from '../../components/ui/EmptyState';
import ErrorBanner from '../../components/ui/ErrorBanner';
import CurrencyDisplay from '../../components/shared/CurrencyDisplay';
import DateDisplay from '../../components/shared/DateDisplay';
import { useAuth } from '../../auth/AuthContext';
import { WorkflowStatus } from '../../enums';
import { listExpensesPaginated, listMyExpensesPaginated, filterExpenses, bulkUploadExpenses } from '../../api/expenses';
import ExpenseForm from './ExpenseForm';
import ExpenseDetailDrawer from './ExpenseDetailDrawer';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  ...Object.values(WorkflowStatus).map((s) => ({ value: s, label: s.replace(/_/g, ' ') })),
];

const DEPT_OPTIONS = [
  { value: '', label: 'All departments' },
  { value: 'FINANCE', label: 'Finance' },
  { value: 'HR', label: 'HR' },
  { value: 'IT', label: 'IT' },
  { value: 'PAYROLL', label: 'Payroll' },
  { value: 'PROCUREMENT', label: 'Procurement' },
];

export default function ExpensesListPage() {
  const { isAdmin } = useAuth();
  const [scope, setScope] = useState(isAdmin ? 'all' : 'mine');
  
  const [statusFilter, setStatusFilter] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  
  const [showForm, setShowForm] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleBulkUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBulkError(null);
    try {
      setIsUploading(true);
      await bulkUploadExpenses(file);
      toast.success('Bulk upload successful!');
      queryClient.invalidateQueries(['expenses']);
    } catch (err) {
      console.error('Bulk upload failed', err);
      setBulkError(err?.message || 'Bulk upload failed. Please check the file format or contents.');
      setShowBulkModal(true);
    } finally {
      setIsUploading(false);
      event.target.value = null; // reset
    }
  };

  const fetchExpenses = async ({ queryKey }) => {
    const [_key, qScope, qStatus, qDept, qPage, qSize] = queryKey;
    const params = { page: qPage, size: qSize };
    if (qStatus) params.status = qStatus;
    if (qDept && qScope === 'all') params.department = qDept;
    
    if (qStatus || (qDept && qScope === 'all')) {
       // Workaround for backend JDBC issue with null dates in Postgres
       params.startDate = '2000-01-01';
       params.endDate = '2099-12-31';
       return filterExpenses(params);
    }
    return qScope === 'all' ? listExpensesPaginated(params) : listMyExpensesPaginated(params);
  };

  const { data: pageData, isLoading, error } = useQuery({
    queryKey: ['expenses', scope, statusFilter, deptFilter, page, pageSize],
    queryFn: fetchExpenses,
    enabled: isAdmin || scope === 'mine',
  });

  const content = pageData?.content || (Array.isArray(pageData) ? pageData : []);
  const totalPages = pageData?.totalPages || 0;

  const columns = [
    {
      key: 'title',
      header: 'Title',
      render: (r) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-slate-800">{r.title}</div>
          <div className="truncate text-xs text-slate-500">{r.category} {r.vendorName ? ` • ${r.vendorName}` : ''}</div>
        </div>
      ),
    },
    { key: 'department', header: 'Dept' },
    { 
      key: 'createdByUsername', 
      header: 'Submitter',
      render: (r) => <span className="text-slate-600">{r.createdByUsername}</span>
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (r) => <CurrencyDisplay value={r.amount} className="font-medium" />,
    },
    {
      key: 'workflowStatus',
      header: 'Status',
      render: (r) => <StatusPill value={r.workflowStatus} />,
    },
    {
      key: 'transactionDate',
      header: 'Date',
      render: (r) => <DateDisplay value={r.transactionDate || r.createdAt} />,
    },
  ];

  return (
    <AppLayout title="Expenses" breadcrumb="Finance / Expenses">
      <PageHeader
        title={scope === 'mine' ? 'My Expenses' : 'All Expenses'}
        description={
          isAdmin
            ? 'Enterprise-wide expense tracking and approvals.'
            : 'Log expenses, attach receipts, and track approval status.'
        }
        actions={
          <div className="flex gap-2 items-center">
            <Button 
              icon={FiUpload} 
              onClick={() => setShowBulkModal(true)} 
              variant="outline" 
              className="bg-white text-slate-700 hover:bg-slate-50 border-slate-300"
              disabled={isUploading}
            >
              {isUploading ? 'Uploading...' : 'Upload Excel'}
            </Button>
            <input 
              type="file" 
              ref={fileInputRef} 
              className="hidden" 
              accept=".xlsx, .xls" 
              onChange={(e) => {
                handleBulkUpload(e);
                setShowBulkModal(false);
              }} 
            />
            <Button icon={FiPlus} onClick={() => setShowForm(true)} className="bg-slate-900 hover:bg-slate-800 text-white border-0 shadow-sm">
              New Expense
            </Button>
          </div>
        }
      />

      {error && (
        <div className="mb-6">
          <ErrorBanner error={error} title="Failed to load expenses" />
        </div>
      )}

      {isAdmin && (
        <div className="mb-6 flex gap-1 border-b border-slate-200 pb-px">
          <button
            type="button"
            onClick={() => { setScope('all'); setPage(0); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              scope === 'all'
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            All Expenses
          </button>
          <button
            type="button"
            onClick={() => { setScope('mine'); setPage(0); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              scope === 'mine'
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            My Expenses
          </button>
        </div>
      )}

      <div className="mb-6 flex flex-wrap items-center gap-4 bg-white p-4 rounded-lg shadow-sm border border-slate-200">
        <Select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          className="sm:w-48 bg-slate-50"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </Select>
        
        {scope === 'all' && (
          <Select
            value={deptFilter}
            onChange={(e) => { setDeptFilter(e.target.value); setPage(0); }}
            className="sm:w-48 bg-slate-50"
          >
            {DEPT_OPTIONS.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </Select>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <DataTable
          columns={columns}
          rows={content}
          loading={isLoading}
          keyField="id"
          onRowClick={(row) => setDetailId(row.id)}
          empty={
            <EmptyState
              icon={FiCreditCard}
              title="No expenses found"
              description="There are no expenses matching the current criteria."
              action={
                <Button icon={FiPlus} onClick={() => setShowForm(true)} variant="outline">
                  New Expense
                </Button>
              }
            />
          }
        />
        
        {/* Pagination Controls */}
        {(content.length > 0 || page > 0) && (
          <div className="flex flex-col sm:flex-row items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-3">
            <div className="flex items-center gap-3 mb-3 sm:mb-0">
              <span className="text-sm text-slate-500">
                Page Size:
              </span>
              <select 
                value={pageSize} 
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(0);
                }}
                className="w-20 px-2 py-1 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary outline-none"
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500 mr-2">
                Page <span className="font-medium text-slate-700">{page + 1}</span> of <span className="font-medium text-slate-700">{totalPages}</span>
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                icon={FiChevronLeft} 
                disabled={page === 0} 
                onClick={() => setPage(p => Math.max(0, p - 1))}
              >
                Previous
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                disabled={page >= totalPages - 1} 
                onClick={() => setPage(p => p + 1)}
              >
                Next <FiChevronRight className="ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {showForm && (
        <ExpenseForm open={showForm} onClose={() => setShowForm(false)} />
      )}

      <ExpenseDetailDrawer
        open={!!detailId}
        expenseId={detailId}
        onClose={() => setDetailId(null)}
      />

      {showBulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="w-full max-w-md bg-blue-50 border border-blue-200 rounded-xl shadow-xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="bg-blue-100/50 p-5 border-b border-blue-200 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-blue-900">Upload Excel</h3>
                <p className="text-sm text-blue-700 mt-1">Upload your expenses via an Excel file (.xlsx, .xls)</p>
              </div>
              <button onClick={() => setShowBulkModal(false)} className="text-blue-400 hover:text-blue-600 p-1 rounded-md hover:bg-blue-100 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <div className="p-5 text-blue-900">
              <p className="text-sm font-medium mb-3">Allowed Excel Headers:</p>
              <div className="bg-white/60 rounded-md border border-blue-100 p-3 text-sm flex flex-wrap gap-2 mb-4">
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">Title</span>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">Amount</span>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">Date</span>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">Category</span>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">Department</span>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">Payment Type</span>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">Description</span>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">Vendor</span>
              </div>
              <p className="text-xs text-blue-700 bg-blue-100/50 p-3 rounded-md border border-blue-200">
                <strong>Note:</strong> All headers except <span className="font-semibold text-blue-900">Title</span> and <span className="font-semibold text-blue-900">Amount</span> are strictly optional. Any missing or unmapped columns will simply be ignored!
              </p>
            </div>
            {bulkError && (
              <div className="mx-5 mb-4 p-3 bg-red-50 text-red-700 rounded-md border border-red-200 text-sm flex items-start gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="mt-0.5 shrink-0" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                <span>{bulkError}</span>
              </div>
            )}
            <div className="p-4 border-t border-blue-200 bg-blue-100/30 flex justify-end gap-3">
              <Button onClick={() => setShowBulkModal(false)} variant="outline" className="border-blue-300 text-blue-800 hover:bg-blue-100">
                Cancel
              </Button>
              <Button 
                onClick={() => fileInputRef.current?.click()} 
                className="bg-blue-600 hover:bg-blue-700 text-white border-0 shadow-sm"
                icon={FiUpload}
              >
                Select File
              </Button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
